import React, {useEffect, useRef, useState} from "react";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {flightService} from "@/services/flightService";
import {isNearAirport} from "@/utils/airports";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {airlineLogo} from "@/utils/airlineLogo";
import {img} from "@/utils/pluginImg.js";

// Light client-side check only — the flight API is the real validator. Same pattern as
// the recommendation chooser: "AC878", "AC 878", "LH1234", "5X123".
const isValidFlightNumber = (v) => /^[A-Za-z0-9]{2,3}\s?\d{1,4}[A-Za-z]?$/.test(v.trim());

// Same wording as the recommendation chooser's warning card (smartPickupRecommendation),
// so the passenger meets one set of messages wherever a flight is checked.
const WARNING_MESSAGES = {
    unavailable: "Failed to get the details from server.",
    cancelled: "Your flight is cancelled. Do you still want to continue?",
    generic: "Your flight doesn't match your selected airport location.",
    wrongDirectionDeparture: "The flight you selected is a departure flight. For a ride from the airport, please select an arrival flight instead.",
};

/**
 * "Enter Flight Number" — the from-airport gate on orderDetails' Continue.
 *
 * A from-airport ride can't be booked without a flight number (the API 422s), and since
 * the date/time step no longer asks for one, this is where the passenger gives it. It ONLY
 * validates the number and puts it on the booking: the pickup time is not touched.
 *
 * Same layout as the smartTimeSelect flight card (`.hopn-flight-manual
 * .hopn-smart-time-select`): "Add" looks the flight up as an ARRIVAL on the ride's date
 * and, if it lands at the pickup airport, shows it underneath (airline · number, from →
 * to). Continue then stores it — FLIGHT_NUMBER (what the booking sends) and
 * SMART_TIME_FLIGHT_DETAILS (orderDetails' flight card) — and `onAdded` carries on with
 * Continue. Nothing is stored until then, so closing the modal leaves the booking as it was.
 *
 * A flight that doesn't land at the pickup airport, a cancelled one, or a provider outage
 * shows the chooser's warning card with "Proceed anyway" (same UI and wording); proceeding
 * accepts it. An outage has no details to show, so only the typed number goes on the booking.
 */
export function FlightNumberPrompt({open, onClose, onAdded, setStep}) {
    const [flightNumber, setFlightNumber] = useState("");
    const [flight, setFlight] = useState(null);           // validated lookup result
    const [unavailable, setUnavailable] = useState(false); // provider down → number accepted as typed
    // Warning awaiting "Proceed anyway": {reason, fd} — fd is the flight to accept (null on an outage).
    const [block, setBlock] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    // Fresh every time it opens.
    useEffect(() => {
        if (!open) return;
        setFlightNumber(storage.get(STORAGE_KEYS.FLIGHT_NUMBER) || "");
        setFlight(null);
        setUnavailable(false);
        setBlock(null);
        setError("");
        setLoading(false);
        const t = setTimeout(() => inputRef.current?.focus(), 200);
        return () => clearTimeout(t);
    }, [open]);

    const pickupAirportCode = () => {
        const first = storage.get(STORAGE_KEYS.DESTINATIONS, [])[0];
        const airport = first?.lat && first?.lng ? isNearAirport(first.lat, first.lng) : null;
        return airport?.code ? String(airport.code).toUpperCase() : null;
    };

    const reset = () => {
        setFlight(null);
        setUnavailable(false);
        setBlock(null);
        setError("");
    };

    const lookup = async () => {
        if (loading) return;
        reset();
        const num = flightNumber.trim();
        if (!num) {
            setError("Please enter your flight number.");
            return;
        }
        if (!isValidFlightNumber(num)) {
            setError("Please enter a valid flight number, e.g. AC878.");
            return;
        }

        setLoading(true);
        const rideDate = String(storage.get(STORAGE_KEYS.RIDE_DATE) || "").slice(0, 10);
        const result = await flightService.getFlightDetails(num, rideDate || undefined, "Arrival");
        setLoading(false);

        if (!result.success) {
            if (result.unavailable) { setBlock({reason: "unavailable", fd: null}); return; }
            setError(result.error || "Failed to fetch flight details.");
            return;
        }
        const fd = result.data?.[0];
        if (!fd) {
            setError("No flight found for that number and date.");
            return;
        }

        const code = pickupAirportCode();
        const arr = (fd?.arrival?.airport?.iata || "").toUpperCase();
        const dep = (fd?.departure?.airport?.iata || "").toUpperCase();
        if (code && arr && arr !== code) {
            setBlock({reason: dep === code ? "wrongDirectionDeparture" : "generic", fd});
            return;
        }
        if (typeof fd?.status === "string" && fd.status.trim().toLowerCase().startsWith("cancel")) {
            setBlock({reason: "cancelled", fd});
            return;
        }

        setFlight(fd);
    };

    // Warning accepted: show the flight (or, on an outage, take the typed number) and let
    // Continue through.
    const proceedAnyway = () => {
        if (!block) return;
        if (block.fd) setFlight(block.fd);
        else setUnavailable(true);
        setBlock(null);
    };

    const confirm = () => {
        if (!flight && !unavailable) return;
        storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber.trim());
        if (flight) storage.set(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, flight);
        else storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
        onAdded();
    };

    const place = (side) => {
        const a = flight?.[side]?.airport;
        const name = a?.municipalityName || a?.name || "—";
        return a?.iata ? `${name} (${a.iata})` : name;
    };
    const local = (side) => flight?.[side]?.scheduledTime?.local;

    return (
        <div className="upper-modal hopn-flight-prompt" style={{display: open ? "flex" : "none"}}>
            <div className="upper-modal-inner">
                <ModalHeader
                    inline={true}
                    title=""
                    extraClasses=""
                    totalSteps={0}
                    cancelButton={true}
                    cancelButtonType="icon"
                    ignoreEditMode={true}
                    backStep={null}
                    showBackButton={false}
                    setStep={setStep}
                    closeModal={onClose}
                />
                <div className="upper-modal-content padding-bottom-30">
                    {/* .hopn-pickup-recommendation brings the chooser's warning card styles. */}
                    <div className="modalPages-spacing hopn-pickup-recommendation">
                        <div className="hopn-flight-manual hopn-smart-time-select">
                            <div className="hopn-flight-input-main">
                                <h2 className="hopn-flight-title">Enter Flight Number</h2>
                                <form
                                    className="hopn-flight-input-wrap"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        // Once a flight is shown, the button is the ✕ that clears it.
                                        if (flight) { reset(); setFlightNumber(""); inputRef.current?.focus(); return; }
                                        lookup();
                                    }}
                                >
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        name="flight_number"
                                        autoComplete="off"
                                        value={flightNumber}
                                        placeholder="E.g. AC4567"
                                        onChange={(e) => { setFlightNumber(e.target.value); reset(); }}
                                    />
                                    <button type="submit" disabled={loading}>
                                        {loading
                                            ? <Loader color="#201e20"/>
                                            : flight
                                                ? <img src={img("bigClose.svg")} alt="Remove"/>
                                                : "Add"}
                                    </button>
                                </form>
                                {error && <div className="error">{error}</div>}
                            </div>

                            {/* Airport mismatch / cancelled flight / provider outage (Proceed anyway). */}
                            {block && (
                                <div className="hopn-pr-warning">
                                    <span>{WARNING_MESSAGES[block.reason] || WARNING_MESSAGES.generic}</span>
                                    <button type="button" className="hopn-pr-warning-proceed" onClick={proceedAnyway}>
                                        Proceed anyway
                                    </button>
                                </div>
                            )}

                            {flight && (
                                <div className="hopn-airlines hopn-flight-prompt-flight">
                                    <div className="hopn-airlines-card">
                                        <div className="hopn-airlines-card-top">
                                            {airlineLogo(flight.airline?.name) && (
                                                <div className="country_icon">
                                                    <img
                                                        className="hopn-airline-flag"
                                                        src={airlineLogo(flight.airline?.name)}
                                                        alt={flight.airline?.name || ""}
                                                    />
                                                </div>
                                            )}
                                            {flight.airline?.name && <>{flight.airline.name}&nbsp;-&nbsp;</>}
                                            <strong>{flight.number || flightNumber.trim().toUpperCase()}</strong>
                                        </div>
                                        <div className="hopn-airlines-card-body">
                                            <div className="hopn-airlines-from">
                                                <span className="hopn-airlines-city">{place("departure")}</span>
                                                <span className="hopn-airlines-time">{local("departure") ? flightService.formatFlightTime(local("departure")) : "—"}</span>
                                                <span className="hopn-airlines-date">{local("departure") ? flightService.formatFlightDate(local("departure")) : ""}</span>
                                            </div>
                                            <div className="hopn-airlines-icon">
                                                <img decoding="async" src={img("plane-2d.svg")} alt=""/>
                                            </div>
                                            <div className="hopn-airlines-to">
                                                <span className="hopn-airlines-city">{place("arrival")}</span>
                                                <span className="hopn-airlines-time">{local("arrival") ? flightService.formatFlightTime(local("arrival")) : "—"}</span>
                                                <span className="hopn-airlines-date">{local("arrival") ? flightService.formatFlightDate(local("arrival")) : ""}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {unavailable && (
                                <p className="hopn-flight-prompt-note">
                                    We couldn't check this flight right now. You can still continue with <strong>{flightNumber.trim().toUpperCase()}</strong>.
                                </p>
                            )}
                        </div>

                        <button
                            type="button"
                            className="hopn-add-passenger-button no-icon hopn-flight-prompt-continue"
                            disabled={!flight && !unavailable}
                            onClick={confirm}
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
